import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { refundApi, refundQueryKeys } from "../../../shared/api/refund";
import { apiError } from "../../../shared/api/client";
import { formatVndWithUnit } from "../../../shared/format";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * Huỷ lịch và hoàn tiền cho bệnh nhân (#18998).
 *
 * <p>Nút chỉ hiện khi SERVER nói được huỷ. Web không tự suy luận "chưa khám và chưa đến ngày hẹn":
 * quy tắc nằm trong {@code invoice_cancellable()} của DB, và một bản sao ở đây sẽ lệch — theo đúng
 * cách đã suýt xảy ra khi tôi viết quy tắc dựa vào `started_at`.
 */
export function CancelInvoiceButton({ invoiceId }: { invoiceId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const check = useQuery({
    queryKey: refundQueryKeys.inspect(invoiceId),
    queryFn: () => refundApi.inspect(invoiceId),
  });

  const request = useMutation({
    mutationFn: () => refundApi.requestFullRefund(invoiceId, reason.trim()),
    onSuccess: () => {
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  // Không có gì để hoàn thì không có gì để huỷ ở màn này.
  const refundable = (check.data?.payments ?? []).filter((p) => p.refundableVnd > 0);
  if (!check.data?.cancellable || refundable.length === 0) {
    return null;
  }
  const total = refundable.reduce((n, p) => n + p.refundableVnd, 0);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Huỷ &amp; hoàn tiền
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Huỷ và hoàn tiền</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Sẽ hoàn <b className="tabular-nums">{formatVndWithUnit(total)}</b> cho bệnh nhân.
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {refundable.map((p) => (
                <li key={p.paymentId} className="flex justify-between gap-3">
                  <span>{p.method}</span>
                  <span className="tabular-nums">{formatVndWithUnit(p.refundableVnd)}</span>
                </li>
              ))}
            </ul>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Lý do huỷ (bệnh nhân bận, đổi lịch…)"
            />
            <p className="text-xs text-muted-foreground">
              Thao tác này <b>ghi ra việc phải làm</b>, chưa chuyển tiền. Khoản hoàn sẽ nằm ở mục
              <b> Hoàn tiền bệnh nhân</b> cho tới khi có người xác nhận đã chuyển trả.
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Đóng
            </Button>
            <Button size="sm" disabled={request.isPending} onClick={() => request.mutate()}>
              {request.isPending ? "Đang ghi…" : "Tạo lệnh hoàn"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
